import { useState, useRef } from "react";
import * as d3 from "d3";
import { ChartHeader } from "@/components/ChartHeader";
import { NoData } from "@/components/NoData";
import { Toggle } from "@/components/Toggle";
import { getKeys } from "@/utils/keys";

import colors from "@/config/colors";
import sectorOptions from "@/config/trading-partners/sectors";
import {
  getEconomyDisplayName,
  getSectorDisplayName,
  formatDisplayList,
  formatPercentage,
  makePossessive,
} from "@/utils/format";
import { useFilter } from "@/hooks/useFilter";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { getSvgElement, useDimensions } from "../utils";
import * as Tooltip from "../Tooltip";
import * as Labels from "../Labels";
import type { ValueAddDataRow } from "../App";
import styles from "./styles.module.scss";

const width = 1200;
const height = 500;
const xAxisHeight = 60;

// McKinsey categorical palette (all 10 colors, reversed so largest values start with electric blue 900)
const colorPalette = [
  "electricBlue900",
  "cyan500",
  "marineGreen300",
  "electricBlue500",
  "marineGreen700",
  "cyan700",
  "orchidPink900",
  "crimsonRed300",
  "marineGreen900",
  "orchidPink400",
] as const;

type LinesByCode = Record<string, ValueAddDataRow[]>;

const directionOptions = [
  { value: "e", label: "Export" },
  { value: "i", label: "Import" },
];

interface ValueAddProps {
  data: ValueAddDataRow[];
  economy: string;
  sector: (typeof sectorOptions)[number];
}

export function ValueAdd({ data, economy, sector }: ValueAddProps) {
  const [tooltipYear, setTooltipYear] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<SVGSVGElement | null>(null);
  const [linesG, setLinesG] = useState<SVGGElement | null>(null);

  const is1600px = useMediaQuery("(min-width: 1600px)");
  const is1200px = useMediaQuery("(min-width: 1200px)");
  const is1024px = useMediaQuery("(min-width: 1024px)");
  const is500px = useMediaQuery("(min-width: 500px)");

  const topPadding = 15;
  const rightPadding = is500px ? 175 : 225;
  const yAxisWidth = is1200px ? 50 : is1024px ? 60 : 65;
  const yAxisInset = is1200px ? 60 : is1024px ? 70 : 80;
  const totalHeight = height + xAxisHeight + topPadding;

  const svgDimensions = useDimensions(svg);
  const linesGDimensions = useDimensions(linesG);

  const { value: direction, onChange: onDirectionChange } = useFilter(
    "dir2",
    directionOptions.map((o) => o.value) as ["e", "i"]
  );

  const selectedDirection = directionOptions.find((o) => o.value === direction);
  const filteredData = data.filter(
    (row) => row.Direction === selectedDirection?.label
  );

  const linesByYear = filteredData.reduce<LinesByCode>((acc, row) => {
    if (acc[row["Year"]]) {
      acc[row["Year"]].push(row);
    } else {
      acc[row["Year"]] = [row];
    }

    return acc;
  }, {} as LinesByCode);

  const partnerOptionsSet = new Set<string>();
  filteredData.forEach((row) => {
    partnerOptionsSet.add(row["Partner code"]);
  });
  const partnerOptions = filteredData.length
    ? Array.from(partnerOptionsSet).sort((a, b) =>
        getEconomyDisplayName(a).localeCompare(getEconomyDisplayName(b))
      )
    : [];

  const lines = getKeys(linesByYear)
    .map((key) =>
      linesByYear[key].sort((a, b) => parseInt(b["Year"]) - parseInt(a["Year"]))
    )
    .map((row, i) => {
      const partnerShares = row.reduce<
        Record<ValueAddDataRow["Partner code"], number>
      >((acc, r) => {
        acc[r["Partner code"]] = r["Partner share"];
        return acc;
      }, {} as Record<ValueAddDataRow["Partner code"], number>);
      const other =
        100 -
        Object.values(partnerShares).reduce((sum, share) => sum + share, 0);

      return {
        ...partnerShares,
        Other: other,
        Year: parseInt(Object.keys(linesByYear)[i]),
      } as typeof partnerShares;
    });

  const xDomain = d3.extent(
    filteredData.map((row) => parseInt(row["Year"]))
  ) as number[];

  const lastYear = xDomain[1];

  const xScale = d3
    .scaleLinear()
    .domain(xDomain)
    .range([0, width - rightPadding]);

  const yScale = d3.scaleLinear().domain([0, 100]).range([height, 0]);

  const sortedPartnerOptions = partnerOptions.length
    ? partnerOptions
        .sort((a, b) => {
          const aShare = lines.reduce((sum, line) => sum + line[a], 0);
          const bShare = lines.reduce((sum, line) => sum + line[b], 0);
          return bShare - aShare;
        })
        .concat("Other")
    : partnerOptions;

  const lineColors = sortedPartnerOptions.map((economyCode, index, arr) =>
    economyCode === "Other"
      ? "graySuperlight"
      : colorPalette[arr.slice(0, index).includes("Other") ? index - 1 : index]
  );

  const stackedLines = d3.stack().keys(sortedPartnerOptions);

  const area = d3
    .area<ReturnType<typeof stackedLines>[number][number]>()
    .x((d) => xScale(d.data["Year"]))
    .y0((d) => yScale(d[0]))
    .y1((d) => yScale(d[1]));

  const calculateTooltipYear = (
    event: React.MouseEvent<SVGSVGElement, MouseEvent>,
    container: HTMLDivElement | null
  ) => {
    const svgElement = getSvgElement(event.target as Element);

    if (!svgElement) {
      setTooltipYear(null);
      return;
    }

    const { width: svgWidth } = svgElement.getBoundingClientRect();
    const yAxisRelativeWidth = (yAxisWidth / width) * svgWidth;
    const yAxisPosition = is1600px ? 0 : yAxisRelativeWidth;

    const rightPaddingRelative =
      ((is1600px ? rightPadding : rightPadding - 50) / width) * svgWidth;

    const [x] = d3.pointer(event, container);

    if (x < yAxisPosition || x > svgWidth - rightPaddingRelative) {
      setTooltipYear(null);
      return;
    }

    const position =
      ((x - yAxisPosition) / (svgWidth - yAxisRelativeWidth)) * width;
    const year = Math.round(xScale.invert(position));

    // Make sure the year is within the domain, e.g. when hovering the axis d3
    // sometimes goes below the min value
    setTooltipYear(Math.max(xDomain[0], Math.min(xDomain[1], year)));
  };

  const yAxisInsetRelative = is1600px ? 0 : yAxisInset;

  // The x position of the year relative to the container
  const yearX =
    ((xScale(tooltipYear || lastYear) + yAxisInsetRelative) /
      (width + yAxisWidth)) *
    svgDimensions.width;

  // The xAxis height in pixel (instead of svg units)
  const relativeXAxisHeight =
    (xAxisHeight / totalHeight) * svgDimensions.height;
  const topPaddingRelative = (topPadding / totalHeight) * svgDimensions.height;

  const displaySector = getSectorDisplayName(sector);

  const labels = stackedLines(lines)
    .reverse()
    .map((l) => {
      const line = l.find(
        (li) => li.data["Year"] === (tooltipYear || lastYear)
      );
      const economyCode = l.key;

      const share = line?.data?.[economyCode];

      const svgY0 = yScale(line?.[0] || 0);
      const svgY1 = yScale(line?.[1] || 0);
      const svgY = (svgY0 + svgY1) / 2;

      const y = (svgY / totalHeight) * svgDimensions.height;

      return {
        economyCode,
        share,
        y,
        colorName: lineColors[l.index] as (typeof lineColors)[number],
      };
    });

  const displayDirection = selectedDirection?.label || "Import";
  const verb = direction === "e" ? "goes to" : "comes from";

  return (
    <div>
      <ChartHeader
        title={`${displayDirection}ed value-added of ${displaySector.toLowerCase()}`}
        description={`See where the value-added of ${makePossessive(
          getEconomyDisplayName(economy)
        )} ${displaySector.toLowerCase()} ${
          displayDirection?.toLowerCase() || ""
        }s ${verb} and how it changes over time. Value-added is the additional value generated by an industry less the value of the inputs used.`}
      >
        <Toggle
          label="Direction of trade"
          options={directionOptions}
          value={direction}
          onValueChange={(value) => {
            if (value) {
              onDirectionChange(value);
            }
          }}
        />
      </ChartHeader>

      {!lines.length ? (
        <NoData />
      ) : (
        <>
          <div className={styles["chart-wrap"]} ref={containerRef}>
            <span className={styles.hide}>
              {`The top economies that have contributed to the value chain of ${displaySector.toLowerCase()} imports from ${getEconomyDisplayName(
                economy
              )} in order of largest to smallest are ${formatDisplayList(
                labels
                  .filter((l) => l.economyCode !== "Other")
                  .reverse()
                  .map((label) => getEconomyDisplayName(label.economyCode))
              )}. In ${tooltipYear || lastYear}, ${formatDisplayList(
                labels.map(
                  (label) =>
                    `${getEconomyDisplayName(
                      label.economyCode
                    )} accounted for ${
                      label.share
                        ? formatPercentage(label.share / 100)
                        : "(no data)"
                    }`
                )
              )}.`}
            </span>
            <svg
              viewBox={`0 0 ${width + yAxisWidth} ${totalHeight}`}
              onMouseEnter={(event) =>
                calculateTooltipYear(event, containerRef.current)
              }
              onMouseMove={(event) =>
                calculateTooltipYear(event, containerRef.current)
              }
              onMouseLeave={() => {
                setTooltipYear(null);
              }}
              ref={(ref) => setSvg(ref)}
              aria-hidden
            >
              <g transform={`translate(0, ${topPadding})`}>
                <g
                  ref={(ref) => setLinesG(ref)}
                  transform={`translate(${yAxisInset}, 0)`}
                >
                  <g>
                    {stackedLines(lines).map((l, i) => (
                      <path
                        key={l.key}
                        d={area(l) || ""}
                        fill={colors[lineColors[i]]}
                        stroke={colors[lineColors[i]]}
                        strokeWidth="3"
                      />
                    ))}
                  </g>

                  <g
                    transform={`translate(0, ${height})`}
                    className={styles["axis"]}
                  >
                    {xScale.ticks(5).map((tick) => {
                      const x = xScale(tick);
                      return (
                        <g
                          key={tick}
                          transform={`translate(${x} 0)`}
                          textAnchor={
                            x < 20
                              ? "start"
                              : x > width - 100
                              ? "end"
                              : "middle"
                          }
                          className={styles["tick"]}
                        >
                          <text y="40">{tick}</text>
                        </g>
                      );
                    })}
                  </g>
                </g>

                <g
                  transform={`translate(${yAxisWidth - 15}, 0)`}
                  className={styles["axis"]}
                >
                  {yScale.ticks(5).map((tick) => {
                    const y = yScale(tick);
                    return (
                      <g
                        key={tick}
                        transform={`translate(10, ${y})`}
                        className={styles["tick"]}
                      >
                        <text x={-yAxisWidth + 10} dominantBaseline="middle">
                          {tick}%
                        </text>
                      </g>
                    );
                  })}
                </g>
              </g>
            </svg>
            {tooltipYear ? (
              <Tooltip.Root
                x={yearX}
                bottom={relativeXAxisHeight}
                top={topPaddingRelative}
                year={tooltipYear}
                containerX={svgDimensions.x}
              >
                {labels.map((label) => (
                  <Tooltip.Item
                    key={label.economyCode}
                    economyCode={label.economyCode}
                    value={label.share}
                    isPercent
                    colorName={label.colorName}
                    y={label.y}
                  />
                ))}
              </Tooltip.Root>
            ) : (
              <Labels.Root
                x={yearX}
                bottom={relativeXAxisHeight}
                top={topPaddingRelative}
                left={linesGDimensions.right}
              >
                {labels.map((label) => (
                  <Labels.Item
                    // We need to combine economy code and y to make sure the
                    // component gets re-created if the filters change and this
                    // economy code is still in the new data
                    key={`${label.economyCode}-${label.y}`}
                    economyCode={label.economyCode}
                    colorName={label.colorName}
                    y={label.y}
                  />
                ))}
              </Labels.Root>
            )}
          </div>
        </>
      )}
    </div>
  );
}
